import React, { FC, useMemo, useRef, useState } from 'react'

import clsx from 'clsx'
import { useClickAway } from 'react-use'
import styled, { css } from 'styled-components'

import { SearchedUserList } from 'src/components/molecules'

import { useUser } from 'src/hooks'
import { hasElementInId } from 'src/libs/helper'
import { ProfileEntity, UserEntity } from 'src/types'

const Container = styled.div<{ isFocus: boolean; rounded?: boolean }>`
  width: 100%;
  min-height: 3.57rem;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.05);
  border-radius: ${props => (props.rounded ? '0.29rem' : '0')};
  max-height: 37.5rem;
  padding: 0.86rem;
  transition: all 0.1s ease-in-out;
  position: relative;
  display: flex;
  flex-wrap: wrap;

  &:hover:not(:focus):not(:disabled) {
    box-shadow: inset 0 0 8px rgba(0, 0, 0, 0.1);
  }

  ${props =>
    props.isFocus &&
    props.rounded &&
    css`
      border-radius: 0.29rem 0.29rem 0 0;
    `}
`

const ContentInput = styled.input`
  outline: none;
  resize: none;
  height: 2em;
  line-height: 2em;
  width: 100%;
`
const SearchedContainer = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  transform: translateY(100%);
  z-index: 100;
  filter: drop-shadow(0px 5px 20px rgba(122, 137, 160, 0.1));
  width: 100%;
`

type Props = {
  targetUsers: UserEntity[]
  excludedUsers?: UserEntity[]
  profile?: ProfileEntity
  onSelectUser: (user: UserEntity) => void
  onBatchSelect?: (users: UserEntity[]) => void
  rounded?: boolean
  className?: string
}

export const SelectAssignUsersInput: FC<Props> = ({
  targetUsers,
  excludedUsers = [],
  profile,
  onSelectUser,
  onBatchSelect,
  rounded,
  className,
}) => {
  const SearchInputId = 'user-search-input'

  const [inputText, setInputText] = useState<string>('')
  const [isFocus, setIsFocus] = useState(false)
  const [showSearchUsers, setShowSearchUsers] = useState(false)
  const { searchUsers, isInternalUser } = useUser()

  // 空白文字とカンマで分割して、空文字を除外
  const searchWords = useMemo(() => inputText.split(/[\s,]/).filter(Boolean), [inputText])

  const searchedUsers: UserEntity[] = useMemo(() => {
    const users = targetUsers.filter(
      // 検索対象から除外するユーザーを除外
      user => !excludedUsers.some(excludedUser => user.id === excludedUser.id)
    )
    return inputText ? searchUsers({ users, searchWord: inputText }) : users
  }, [targetUsers, inputText, searchUsers, excludedUsers])

  /** 検索したユーザーの中から選択した時 */
  const handleOnSearchedUser = (user: UserEntity) => {
    const searchedUser = targetUsers.find(u => u.id === user.id)
    if (!searchedUser) {
      return
    }
    onSelectUser(searchedUser)
  }

  const handleOnFocusInput = () => {
    setIsFocus(true)
    setShowSearchUsers(true)
  }

  const ref = useRef(null)
  // 検索結果の外をクリックした時
  useClickAway(ref, event => {
    // クリックした要素が検索を行うためのinputだった場合は何もしない
    const target = event.target as HTMLElement
    if (target && hasElementInId(target, SearchInputId)) {
      return
    }

    if (showSearchUsers) {
      setShowSearchUsers(false)
      setIsFocus(false)
    }
  })

  const placeholderText = useMemo(
    () =>
      targetUsers.length > 0 && isInternalUser(targetUsers[0])
        ? 'お名前・メールアドレス・カスタムロール・キーワードで検索（複数可）'
        : 'お名前・メールアドレス・キーワードで検索（複数可）',
    [isInternalUser, targetUsers]
  )

  return (
    <div className={className}>
      <div className="tw-relative">
        <Container
          isFocus={isFocus}
          rounded={rounded}
          className={clsx(
            'tw-border tw-border-border-secondary tw-bg-surface-primary',
            isFocus && 'tw-border-roovPrimary-default'
          )}
        >
          <ContentInput
            id={SearchInputId}
            placeholder={placeholderText}
            value={inputText}
            onChange={event => setInputText(event.target.value)}
            onFocus={handleOnFocusInput}
            className="tw-caret-correctGreen-default"
          />
        </Container>

        {showSearchUsers && (
          <SearchedContainer ref={ref}>
            <SearchedUserList
              users={searchedUsers}
              profile={profile}
              searchWords={searchWords}
              onSelect={handleOnSearchedUser}
              onBatchSelect={onBatchSelect}
              isShowBatchSelect
            />
          </SearchedContainer>
        )}
      </div>
    </div>
  )
}
